import { applyProductAdjustments } from '@/editor/productRow';
import { parseCurrencyAmount } from '@/lib/format';
import type { ProductSnapshot } from '@/types/product';

/**
 * Wat het product in de catalogus zegt dat deze regel zou moeten zijn.
 *
 * De bedragen in een offerte zijn een momentopname: sales mag ze aanpassen en dat
 * verandert het product niet. Daardoor kan een regel stilletjes afwijken van de
 * catalogus. Deze module rekent terug wat het product er nú van zou maken, zodat
 * het zijpaneel het verschil kan tonen.
 *
 * Aantal en korting van de regel tellen mee: die zijn bewust gekozen en maken een
 * regel dus niet "afwijkend".
 */
export interface ProductOriginalValues {
  /** Uurtarief in euro's; null als het product geen uren kent. */
  rate: number | null;
  hours: number | null;
  amount: number | null;
}

export interface RowComparisonInput {
  /** Omschrijving van de offerteregel; koppelt de regel aan een productregel. */
  title: string;
  pricingType: 'hourly' | 'fixed' | 'recurring';
  quantity?: number | null;
  discountPct?: number | null;
}

/** Vergelijkt omschrijvingen los van hoofdletters, spaties en variant-suffix. */
function normalizeDescription(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

function findLine(snapshot: ProductSnapshot, title: string) {
  if (snapshot.rowMode === 'collapsed') return null;
  const wanted = normalizeDescription(title);
  if (!wanted) return null;
  return (
    snapshot.lines.find((line) => normalizeDescription(line.description) === wanted) ??
    snapshot.lines.find((line) => normalizeDescription(line.description).startsWith(wanted)) ??
    null
  );
}

export function productOriginalForRow(
  snapshot: ProductSnapshot,
  input: RowComparisonInput
): ProductOriginalValues | null {
  const options = {
    quantity: input.quantity ?? 1,
    discountPct: input.discountPct ?? 0,
  };
  const line = findLine(snapshot, input.title);

  if (input.pricingType === 'hourly') {
    if (line) {
      const rate = line.hours ? line.totalCents / line.hours : 0;
      return {
        rate: (rate * (1 - options.discountPct / 100)) / 100,
        hours: line.hours * options.quantity,
        amount: applyProductAdjustments(line.totalCents, options) / 100,
      };
    }
    if (snapshot.priceCents === null) return null;
    const blended = snapshot.totalHours > 0 ? snapshot.priceCents / snapshot.totalHours : 0;
    return {
      rate: (blended * (1 - options.discountPct / 100)) / 100,
      hours: snapshot.totalHours * options.quantity,
      amount: applyProductAdjustments(snapshot.priceCents, options) / 100,
    };
  }

  const cents = line ? line.totalCents : snapshot.priceCents;
  if (cents === null) return null;
  return { rate: null, hours: null, amount: applyProductAdjustments(cents, options) / 100 };
}

/**
 * Zoals productOriginalForRow, maar alleen als de regel eenduidig bij het product
 * hoort: een productregel met dezelfde omschrijving, of de productnaam zelf.
 *
 * Dit is de bron voor "prijs bijwerken". Een regel die sales heeft hernoemd, valt
 * anders terug op het producttotaal, en dan zou één deliverable ineens de prijs
 * van de hele website krijgen.
 */
export function productLatestForRow(
  snapshot: ProductSnapshot,
  input: RowComparisonInput
): ProductOriginalValues | null {
  const namesProduct = normalizeDescription(input.title).startsWith(
    normalizeDescription(snapshot.productName)
  );
  if (
    snapshot.rowMode !== 'collapsed' &&
    !findLine(snapshot, input.title) &&
    snapshot.lines.length > 0 &&
    !namesProduct
  ) {
    return null;
  }
  return productOriginalForRow(snapshot, input);
}

/** Getal uit een cel; lege tekst en vrije tekst zoals “Op maat” geven null. */
export function parseCellNumber(value: string | undefined): number | null {
  const raw = (value ?? '').trim();
  if (!raw) return null;
  if (/[€£$]/.test(raw)) {
    const parsed = parseCurrencyAmount(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }
  const normalized = raw.replace(/\./g, '').replace(',', '.');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function differs(current: number | null, original: number | null): boolean {
  if (original == null) return false;
  if (current == null) return true;
  return Math.abs(current - original) > 0.005;
}

/**
 * Welke velden van de regel niet meer overeenkomen met het product.
 * Lege lijst betekent: de regel staat nog precies op de catalogusprijs.
 */
export function changedFields(
  cells: string[],
  pricingType: 'hourly' | 'fixed' | 'recurring',
  original: ProductOriginalValues
): Array<'rate' | 'hours' | 'amount'> {
  const changed: Array<'rate' | 'hours' | 'amount'> = [];

  if (pricingType === 'hourly') {
    if (differs(parseCellNumber(cells[1]), original.rate)) changed.push('rate');
    if (differs(parseCellNumber(cells[2]), original.hours)) changed.push('hours');
    if (differs(parseCellNumber(cells[3]), original.amount)) changed.push('amount');
    return changed;
  }

  if (pricingType === 'recurring') {
    if (differs(parseCellNumber(cells[1]), original.amount)) changed.push('amount');
  }

  return changed;
}
